import { API_BASE_URL, handleResponse } from './client';
import { getAuthToken } from './authApi';

export async function uploadAnalysis({ inputType, sampleName, files, evalue, minIdentity }) {
  const formData = new FormData();
  formData.append('inputType', inputType);
  formData.append('sampleName', sampleName);
  if (evalue) formData.append('evalue', evalue);
  if (minIdentity) formData.append('minIdentity', minIdentity);
  files.forEach((file) => formData.append('files', file));

  const res = await fetch(`${API_BASE_URL}/analysis/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: formData,
  });
  return handleResponse(res);
}

export async function getAnalysisStatus(jobId) {
  const res = await fetch(`${API_BASE_URL}/analysis/status/${jobId}`);
  return handleResponse(res);
}

export async function getAnalysisResults(jobId) {
  const res = await fetch(`${API_BASE_URL}/analysis/results/${jobId}`);
  return handleResponse(res);
}

export function getDownloadUrl(jobId, filename) {
  return `${API_BASE_URL}/analysis/download/${jobId}/${filename}`;
}

// Network errors (ERR_NETWORK_CHANGED, offline blips, dropped connections) surface as
// a TypeError "Failed to fetch". These are transient and must NOT end polling — the job
// keeps running server-side. We only give up after several consecutive failures.
const MAX_CONSECUTIVE_ERRORS = 5;

function isTransientNetworkError(err) {
  return err instanceof TypeError; // fetch throws TypeError for network-level failures
}

export function pollUntilDone(jobId, { intervalMs = 2000, onStatusChange } = {}) {
  return new Promise((resolve, reject) => {
    let consecutiveErrors = 0;

    const tick = async () => {
      try {
        const status = await getAnalysisStatus(jobId);
        consecutiveErrors = 0; // any success resets the counter
        onStatusChange?.(status);

        if (status.status === 'completed') return resolve(status);
        if (status.status === 'failed') {
          return reject(new Error(status.error || 'Pipeline failed'));
        }
        setTimeout(tick, intervalMs);
      } catch (err) {
        consecutiveErrors += 1;

        // A real pipeline failure comes back as status.failed above, not as a thrown
        // error. A thrown error here is almost always a transient network/HTTP blip.
        if (isTransientNetworkError(err) && consecutiveErrors < MAX_CONSECUTIVE_ERRORS) {
          console.warn(
            `Polling network blip (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}), retrying:`,
            err.message
          );
          // brief backoff so a longer outage doesn't hammer the server
          setTimeout(tick, intervalMs + consecutiveErrors * 1000);
          return;
        }

        if (consecutiveErrors < MAX_CONSECUTIVE_ERRORS) {
          // non-network error but still under the limit — retry once more on normal interval
          setTimeout(tick, intervalMs);
          return;
        }

        reject(
          new Error(
            'Lost connection to the server while checking analysis status. ' +
              'The job may still be running — check the Results tab in a moment.'
          )
        );
      }
    };

    tick();
  });
}